import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
  action?: {
    type: 'navigate' | 'external' | 'modal';
    target: string;
    projectId?: string;
  };
}

interface AlertNotificationRow {
  id: string;
  title: string;
  content: string;
  is_read: boolean;
  created_at: string;
  alert_config_id: string | null;
}

const NOTIFICATIONS_QUERY_KEY = ['alert_notifications'];

const inferType = (title: string): Notification['type'] => {
  const t = title.toLowerCase();
  if (t.includes('error') || t.includes('falla') || t.includes('crítico')) return 'error';
  if (t.includes('vencid') || t.includes('alert') || t.includes('atras')) return 'warning';
  if (t.includes('completad') || t.includes('éxito') || t.includes('listo')) return 'success';
  return 'info';
};

export const useNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const { data: notifications = [] } = useQuery<Notification[]>({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, user?.id],
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('alert_notifications')
        .select('id, title, content, is_read, created_at, alert_config_id')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return ((data as AlertNotificationRow[]) ?? []).map((n) => ({
        id: n.id,
        title: n.title,
        message: n.content,
        type: inferType(n.title),
        timestamp: new Date(n.created_at),
        read: n.is_read,
      }));
    },
  });

  // Realtime subscription: refresh notifications when new alerts arrive
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`alert_notifications-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'alert_notifications' },
        () => {
          queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('alert_notifications')
        .update({ is_read: true })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;
      const { error } = await supabase
        .from('alert_notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('alert_notifications').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  });

  const clearAllMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;
      const { error } = await supabase
        .from('alert_notifications')
        .delete()
        .eq('user_id', user.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  });

  const markAsRead = useCallback((id: string) => markReadMutation.mutate(id), [markReadMutation]);
  const markAllAsRead = useCallback(() => markAllReadMutation.mutate(), [markAllReadMutation]);
  const removeNotification = useCallback((id: string) => removeMutation.mutate(id), [removeMutation]);
  const clearAll = useCallback(() => clearAllMutation.mutate(), [clearAllMutation]);
  const toggleDropdown = useCallback(() => setIsOpen((p) => !p), []);

  // Optional: noop placeholder kept for backwards compatibility
  const addNotification = useCallback((_n: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    // Notifications are now persisted via Supabase by edge functions / triggers
  }, []);

  return {
    notifications,
    unreadCount,
    isOpen,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
    toggleDropdown,
    setIsOpen,
    checkSmartNotifications: () => Promise.resolve(),
    isCheckingSmartNotifications: false,
  };
};
